import Vue from 'vue'
import App from './App.vue'
import router from './router/router'
import store from './store/store'
import { i18n } from './internationalization/i18nSetup'

import BootstrapVue from 'bootstrap-vue'
import 'bootstrap/dist/css/bootstrap.css'
import 'bootstrap-vue/dist/bootstrap-vue.css'

import CoozzyComponents from './misc/loader/global/frameworkComponents'
import CoozzyFilter from './misc/loader/global/filter'
import CoozzyLayout from './misc/loader/global/frameworkLayout'

import AuthPlugin from './misc/plugins/auth0'

import FormWizard from 'vue-form-wizard'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import * as VueGoogleMaps from 'vue2-google-maps'
import Clipboard from 'v-clipboard'
import Vuelidate from 'vuelidate'
import VueTextareaAutosize from 'vue-textarea-autosize'
import * as Sentry from '@sentry/vue'
import type { Event as SentryEvent } from '@sentry/vue'
import { startMemoryMonitor } from './misc/utils/memoryMonitor'
import GmapCluster from 'vue2-google-maps/dist/components/cluster' // replace src with dist if you have Babel issues
import Toasted from 'vue-toasted'
import InstantSearch from 'vue-instantsearch'
import VueCountryRegionSelect from '@coozzy/vue-country-region-select'
import browserDetect from 'vue-browser-detect-plugin'
import VueMeta from 'vue-meta'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import VueDOMPurifyHTML from 'vue-dompurify-html'
import VTooltip from 'v-tooltip'
import 'viewerjs/dist/viewer.css'
import VueViewer from 'v-viewer'
import process from 'process'

if (typeof window !== 'undefined' && !(window as any).process) {
  ;(window as any).process = process
}

const DEFAULT_TOAST_DURATION_MS = 5000
const TOAST_DURATION_BY_TYPE: Record<string, number> = {
  success: 2000
}

Vue.use(VTooltip)
Vue.use(VueViewer)

Vue.use(BootstrapVue)
Vue.use(CoozzyLayout)
Vue.use(CoozzyComponents)
Vue.use(CoozzyFilter)
Vue.use(AuthPlugin)
Vue.use(Clipboard)
Vue.use(Vuelidate)
Vue.use(VueTextareaAutosize)
Vue.use(FormWizard)
Vue.use(VueGoogleMaps, {
  load: {
    key: process.env.VUE_APP_GOOGLE_API_KEY,
    libraries: 'places'
  }
})
Vue.use(Toasted, {
  position: 'top-center',
  duration: DEFAULT_TOAST_DURATION_MS,
  singleton: true
})
const originalToastedShow = Vue.toasted.show.bind(Vue.toasted)
Vue.toasted.show = (...args: any[]) => {
  const [message, options] = args

  if (typeof options === 'object' && options !== null) {
    const typedOptions = options as { type?: string, duration?: number }
    const durationByType = typedOptions.type ? TOAST_DURATION_BY_TYPE[typedOptions.type] : undefined

    if (durationByType !== undefined && typedOptions.duration === undefined) {
      return originalToastedShow(message, {
        ...typedOptions,
        duration: durationByType
      })
    }
  }

  return originalToastedShow(...args)
}
Vue.use(InstantSearch)
Vue.use(VueCountryRegionSelect)
Vue.use(browserDetect)
Vue.use(VueMeta)
Vue.use(VueDOMPurifyHTML, {
  hooks: {
    afterSanitizeAttributes: (node) => {
      // set all elements owning href to target=_blank
      if ('href' in node) {
        node.setAttribute('target', '_blank')
      }
    }
  }
})

Vue.component('v-select', vSelect)
Vue.component('font-awesome-icon', FontAwesomeIcon)
Vue.component('multiselect', Multiselect)
Vue.component('GmapCluster', GmapCluster)

// Drop only third-party circular-JSON noise:
// DOM patches stringify Vue instances. Keep events that include app (/src) frames.
function isThirdPartyCircularJsonNoise(event: SentryEvent): boolean {
  const exception = event.exception?.values?.[0]
  if (!exception?.value || !/circular structure to JSON/i.test(exception.value)) {
    return false
  }

  const frames = exception.stacktrace?.frames ?? []
  if (frames.length === 0) {
    return false
  }

  const hasAppFrame = frames.some((frame) => {
    const filename = frame.filename || ''
    return filename.includes('/src/') || filename.startsWith('webpack:///src/')
  })
  if (hasAppFrame) {
    return false
  }

  const hasAnonymousAppendChild = frames.some((frame) => {
    const filename = frame.filename || ''
    const fn = frame.function || ''
    return filename.includes('<anonymous>') && fn.includes('appendChild')
  })
  const hasAnonymousJsonStringify = frames.some((frame) => {
    const filename = frame.filename || ''
    const fn = frame.function || ''
    return (filename.includes('<anonymous>') || filename === '') && fn === 'JSON.stringify'
  })

  return hasAnonymousAppendChild && hasAnonymousJsonStringify
}

if (process.env.NODE_ENV === 'production') {
  const isStaging = window.location.hostname === 'staging.myprop.ch'

  const hour = new Date().getHours()
  const isNightTime = hour >= 23 || hour < 8
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
  const platform = typeof navigator !== 'undefined' ? navigator.platform || '' : ''
  const isLinux = /linux/i.test(userAgent) || /linux/i.test(platform)
  // disable sampling for cypress night run
  const shouldDisableSampling = isStaging && isNightTime && isLinux

  Sentry.init({
    Vue,
    dsn: 'https://9f61a7cd865d4b60a7705211c8fd9aa9@o251494.ingest.sentry.io/1437729',
    release: process.env.VUE_APP_VERSION || undefined,
    environment: isStaging ? 'staging' : 'production',
    attachProps: true,
    integrations: [
      Sentry.browserTracingIntegration({
        router,
        routeLabel: 'name'
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true
      })
    ],
    tracingOptions: {
      trackComponents: [
        'ObjectVacancy',
        'PropertyTab',
        'PropertyRentComponents',
        'LandRegisterTab',
        'BuildingsTab',
        'ObjectBasic',
        'CoozzyCard',
        'CreateObjectsTab',
        'DevicesTab',
        'Device',
        'CreateDevicesTab',
        'DevicesByRoomTab',
        'DeviceInspection',
        'DeviceInspectionCosts',
        'DeviceMaintenance',
        'DeviceMaintenanceCosts',
        'DeviceRevision',
        'DeviceRevisionCosts'
      ],
      hooks: [
        'mount'
        // 'update' // it's taking many more spans than mount
      ]
    },
    tracePropagationTargets: ['localhost', 'https://staging.myprop.ch', 'https://myprop.ch'],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: shouldDisableSampling ? 0 : 0.3,
    tracesSampler: ({ name }) => {
      if (shouldDisableSampling) return 0
      if (isStaging) return 0.01

      const tx = (name || '').toLowerCase()
      if (!name) return 0.05

      if (/login|logout|callback/.test(tx)) return 0.01
      if (/buildings/.test(tx)) return 0.1

      return 0.05
    },
    beforeSend(event) {
      if (isThirdPartyCircularJsonNoise(event)) {
        return null
      }
      return event
    }
  })

  startMemoryMonitor()
}

Vue.config.productionTip = false

new Vue({
  router,
  store,
  i18n,
  render: h => h(App)
}).$mount('#app')
